import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Observable, Subject } from 'rxjs';
// html2canvas-pro and jspdf are dynamically imported inside
// downloadFullHistoryPdf() so they don't inflate the app's initial bundle.
import { takeUntil } from 'rxjs/operators';
import { ADJUSTMENT_MARKER, isAdjustmentOrder } from '@shared/constants';
import { IOrder, OrderType } from '../../../../../../shared/schemas/orderSchema';
import { UserType, UserTypeWithOrders } from '../../../../../../shared/schemas/userSchema';
import { ConfirmDialogComponent } from '../../../components/buttons/delete-button-with-icon/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from './../../../services/snack-bar.service';
import {
  updateOrderInvoice,
  updateUserinStore,
} from '../../../store/slices/usersSlice/usersSlice.actions';
import {
  selectCurrentUser,
  selectCurrentUserName,
  selectUserById,
} from './../../../store/slices/usersSlice/usersSlice.selector';
import { AppStore } from '../../../store/store';
import { OrdersService } from '../services/orders.service';
import { UsersService } from '../services/users.service';
import labels from './user-detailed.json';

interface MonthGroup {
  month: number;        // 1..12
  monthName: string;    // Hebrew Gregorian month name
  orders: OrderType[];
}
interface YearGroup {
  year: number;
  months: MonthGroup[];
}

const HEBREW_GREGORIAN_MONTHS = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];

@Component({
  selector: 'app-user-detailed',
  templateUrl: './user-detailed.component.html',
  styleUrl: './user-detailed.component.scss',
})
export class UserDetailedComponent implements OnInit, OnDestroy {
  // ── State ───────────────────────────────────────────────────────────
  currentUser$: Observable<UserType | null> = this.store.select(selectCurrentUser);
  currentUserName$: Observable<string> = this.store.select(selectCurrentUserName);
  userDetailes$: Observable<UserTypeWithOrders | undefined> = this.store.select(
    selectUserById(''),
  );
  user: UserTypeWithOrders | null = null;

  // Helper exposed to the template — distinguishes adjustment entries from
  // regular orders so they render as readonly with a different style.
  readonly isAdjustment = isAdjustmentOrder;

  userHasAccess = false;
  loading = false;
  userId = '';

  // i18n
  dateLabel: string = labels.date;
  parahsa: string = labels.parasha;
  amount: string = labels.amount;
  paid: string = labels.paid;
  left: string = labels.left;
  desc: string = labels.description;
  invoice: string = labels.invoiceNo;
  header: string = labels.header;
  actions: string = labels.actions;

  // Order invoice editing
  showEditButton: string | null = null;
  updatedInvoices: Record<string, string> = {};
  @ViewChild('confirmDialog') confirmDialog!: ConfirmDialogComponent;
  @ViewChild('fullHistoryPrint') fullHistoryPrintRef!: ElementRef<HTMLElement>;
  deleteAction: (() => Promise<void>) | undefined;
  fullPdfBusy = false;
  today = new Date();

  // ── Balance adjustment (admin override) ────────────────────────────
  showAdjustDialog = false;
  adjustDirection: 'add' | 'subtract' = 'add';
  adjustAmount: number | null = null;
  adjustReason = '';
  adjustSubmitting = false;

  // ── Filter + pagination state ──────────────────────────────────────
  filterYear: number | null = null;
  filterMonth: number | null = null;
  filterParasha: string | null = null;
  currentPage = 1;
  readonly pageSize = 30;

  // Filter dropdown options (derived from this user's orders).
  availableYears: number[] = [];
  availableMonths: { value: number; label: string }[] = [];
  availableParashot: string[] = [];

  // Derived views (recomputed whenever data or filters change).
  allSortedOrders: OrderType[] = [];
  filteredOrders: OrderType[] = [];
  paginatedOrders: OrderType[] = [];
  groupedOrders: YearGroup[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppStore>,
    private route: ActivatedRoute,
    private userService: UsersService,
    private ordersService: OrdersService,
    private snackBar: SnackBarService,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.userId = params['id'];
      this.userDetailes$ = this.store.select(selectUserById(this.userId));

      // React to store updates for this user (initial + on edits).
      this.userDetailes$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
        if (user) {
          this.user = user;
          this.rebuildFromOrders(user.orders ?? []);
        }
      });
    });

    this.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.userHasAccess = user?.hasAccess ?? false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Filters / pagination / grouping ────────────────────────────────

  private rebuildFromOrders(orders: readonly OrderType[]): void {
    // Latest first.
    this.allSortedOrders = [...orders].sort(
      (a, b) =>
        new Date(b.createdAt as unknown as string).getTime() -
        new Date(a.createdAt as unknown as string).getTime(),
    );

    // Available dropdown values come from this user's actual orders.
    const yearSet = new Set<number>();
    const monthSet = new Set<number>();
    const parashaSet = new Set<string>();
    for (const o of this.allSortedOrders) {
      const d = new Date(o.createdAt as unknown as string);
      yearSet.add(d.getFullYear());
      monthSet.add(d.getMonth() + 1);
      if (o.parasha) parashaSet.add(o.parasha);
    }
    this.availableYears = Array.from(yearSet).sort((a, b) => b - a);
    this.availableMonths = Array.from(monthSet)
      .sort((a, b) => a - b)
      .map((m) => ({ value: m, label: HEBREW_GREGORIAN_MONTHS[m - 1] }));
    this.availableParashot = Array.from(parashaSet).sort((a, b) =>
      a.localeCompare(b, 'he'),
    );

    this.applyFilters();
  }

  private applyFilters(): void {
    let filtered = this.allSortedOrders;

    if (this.filterYear !== null) {
      filtered = filtered.filter(
        (o) =>
          new Date(o.createdAt as unknown as string).getFullYear() ===
          this.filterYear,
      );
    }
    if (this.filterMonth !== null) {
      filtered = filtered.filter(
        (o) =>
          new Date(o.createdAt as unknown as string).getMonth() + 1 ===
          this.filterMonth,
      );
    }
    if (this.filterParasha) {
      filtered = filtered.filter((o) => o.parasha === this.filterParasha);
    }

    this.filteredOrders = filtered;
    // Reset to page 1 whenever filter changes.
    this.currentPage = 1;
    this.paginate();
  }

  private paginate(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    this.paginatedOrders = this.filteredOrders.slice(start, start + this.pageSize);
    this.groupedOrders = this.groupByYearMonth(this.paginatedOrders);
  }

  private groupByYearMonth(orders: OrderType[]): YearGroup[] {
    const byYear = new Map<number, Map<number, OrderType[]>>();
    for (const o of orders) {
      const d = new Date(o.createdAt as unknown as string);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      if (!byYear.has(y)) byYear.set(y, new Map());
      const monthMap = byYear.get(y)!;
      if (!monthMap.has(m)) monthMap.set(m, []);
      monthMap.get(m)!.push(o);
    }

    return Array.from(byYear.entries())
      .sort((a, b) => b[0] - a[0]) // newest year first
      .map(([year, monthMap]) => ({
        year,
        months: Array.from(monthMap.entries())
          .sort((a, b) => b[0] - a[0]) // newest month first
          .map(([month, monthOrders]) => ({
            month,
            monthName: HEBREW_GREGORIAN_MONTHS[month - 1],
            orders: monthOrders,
          })),
      }));
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredOrders.length / this.pageSize));
  }

  get hasFilters(): boolean {
    return (
      this.filterYear !== null ||
      this.filterMonth !== null ||
      this.filterParasha !== null
    );
  }

  get viewWindow(): string {
    if (this.filteredOrders.length === 0) return '0';
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.filteredOrders.length);
    return `${start}–${end}`;
  }

  onYearChange(value: string | number | null): void {
    this.filterYear = value === '' || value === null ? null : Number(value);
    this.applyFilters();
  }

  onMonthChange(value: string | number | null): void {
    this.filterMonth = value === '' || value === null ? null : Number(value);
    this.applyFilters();
  }

  onParashaChange(value: string | null): void {
    this.filterParasha = value === '' || value === null ? null : value;
    this.applyFilters();
  }

  resetFilters(): void {
    this.filterYear = null;
    this.filterMonth = null;
    this.filterParasha = null;
    this.applyFilters();
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1;
      this.paginate();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage -= 1;
      this.paginate();
    }
  }

  goToPage(page: number): void {
    const clamped = Math.max(1, Math.min(this.totalPages, page));
    if (clamped !== this.currentPage) {
      this.currentPage = clamped;
      this.paginate();
    }
  }

  // ── Invoice editing ────────────────────────────────────────────────

  onInputChange(event: Event, orderId: string): void {
    const inputElement = event.target as HTMLInputElement;
    this.updatedInvoices[orderId] = inputElement.value;
    this.showEditButton = orderId;
  }

  saveInvoice(orderId: string): void {
    const newInvoice = this.updatedInvoices[orderId];
    this.onUpdate(orderId, newInvoice);
    this.showEditButton = null;
  }

  private async onUpdate(orderId: string, newInvoice: string): Promise<void> {
    try {
      this.store.dispatch(updateOrderInvoice({ orderId, orderInvoice: newInvoice }));
      await this.ordersService.updateOrderInvoice(orderId, newInvoice);
      this.snackBar.openSnackBar('נשמר בהצלחה', 'x');
    } catch (error) {
      console.error(error);
      this.snackBar.openSnackBarError('לא ניתן לשמור מספר קבלה', 'x');
    }
  }

  // ── Delete order ───────────────────────────────────────────────────

  openDeleteModal(orderId: string): void {
    this.deleteAction = () => this.onDelete(orderId);
    this.confirmDialog.openModal();
  }

  private async onDelete(id: string): Promise<void> {
    this.loading = true;
    try {
      const res = await this.ordersService.deleteOrder(id);
      this.snackBar.openSnackBar('נמחק בהצלחה', 'x');
      this.store.dispatch(updateUserinStore({ user: res }));
    } catch (error) {
      console.error(error);
      this.snackBar.openSnackBarError('לא ניתן למחוק', 'x');
    } finally {
      this.loading = false;
    }
  }

  // ── Balance adjustment (admin manual override) ─────────────────────
  //
  // Records a "התאמת יתרה" entry in the user's order history with the
  // admin's name in `doneBy` and the reason in `note`. Uses the existing
  // ledger math: +N means price=0/pricePaid=N (credit); -N means
  // price=N/pricePaid=0 (debit). No new collection, no schema change.

  openAdjustDialog(): void {
    this.adjustDirection = 'add';
    this.adjustAmount = null;
    this.adjustReason = '';
    this.showAdjustDialog = true;
  }

  closeAdjustDialog(): void {
    this.showAdjustDialog = false;
  }

  setAdjustDirection(direction: 'add' | 'subtract'): void {
    this.adjustDirection = direction;
  }

  onAdjustAmountInput(value: string): void {
    const cleaned = (value ?? '').replace(/[^0-9]/g, '');
    if (cleaned === '') {
      this.adjustAmount = null;
      return;
    }
    const n = Number(cleaned);
    this.adjustAmount = Number.isSafeInteger(n) && n > 0 ? n : null;
  }

  /** Estimate of the user's balance after this adjustment lands. */
  get adjustPreviewBalance(): number | null {
    if (!this.user || this.adjustAmount === null) return null;
    const delta = this.adjustDirection === 'add' ? this.adjustAmount : -this.adjustAmount;
    return (this.user.balance ?? 0) + delta;
  }

  async submitAdjust(): Promise<void> {
    if (
      !this.user ||
      this.adjustAmount === null ||
      this.adjustAmount <= 0 ||
      this.adjustSubmitting
    ) {
      return;
    }
    this.adjustSubmitting = true;

    const amount = this.adjustAmount;
    const isCredit = this.adjustDirection === 'add';

    // Use the existing addOrder ledger semantics:
    //   pricePaid - price = balance impact
    const order: IOrder = {
      name: [ADJUSTMENT_MARKER],
      userId: this.user._id,
      parasha: ADJUSTMENT_MARKER,
      price: isCredit ? 0 : amount,
      pricePaid: isCredit ? amount : 0,
      beenPaid: true,
      note: this.adjustReason?.trim() || undefined,
    };

    try {
      const adminName = await firstValueFrom(this.currentUserName$);
      const payload = { ...order, doneBy: adminName || 'אדמין' };
      const res = await this.ordersService.addOrder(payload);
      this.store.dispatch(updateUserinStore({ user: res }));
      this.snackBar.openSnackBar('היתרה עודכנה', 'x');
      this.showAdjustDialog = false;
    } catch (error) {
      console.error('Balance adjust failed:', error);
      this.snackBar.openSnackBarError('לא ניתן לעדכן יתרה', 'x');
    } finally {
      this.adjustSubmitting = false;
    }
  }

  // ── Print (browser dialog) ─────────────────────────────────────────

  onPrint(): void {
    window.print();
  }

  // ── Full-history PDF ───────────────────────────────────────────────
  //
  // Renders EVERY order (ignoring filters and pagination) as a compact
  // 3-column table on a single A4 portrait page, then captures it via
  // html2canvas-pro and wraps in a jsPDF file. The hidden layout lives
  // off-screen at position:absolute left:-9999px so it has real box
  // measurements without being visible.

  /** Split the sorted-orders list into `n` roughly-equal chunks
   *  (for the 3-column tabular print layout). Order preserved. */
  get fullHistoryColumns(): OrderType[][] {
    const src = this.allSortedOrders ?? [];
    if (src.length === 0) return [[], [], []];
    const cols = 3;
    const per = Math.ceil(src.length / cols);
    const out: OrderType[][] = [];
    for (let i = 0; i < cols; i++) {
      out.push(src.slice(i * per, (i + 1) * per));
    }
    return out;
  }

  async downloadFullHistoryPdf(): Promise<void> {
    if (this.fullPdfBusy) return;
    if (!this.user || !this.allSortedOrders.length) return;

    this.fullPdfBusy = true;
    try {
      // Make sure the hidden layout is rendered/measured.
      await new Promise(requestAnimationFrame);
      await new Promise(requestAnimationFrame);

      const el = this.fullHistoryPrintRef?.nativeElement;
      if (!el) throw new Error('full-history print view not found');

      // Lazy-load the heavy PDF stack only when the user asks for it.
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas-pro'),
        import('jspdf'),
      ]);

      const canvas = await html2canvas(el, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        ignoreElements: (n: Element) =>
          n instanceof HTMLElement && n.classList?.contains('noPrint'),
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.97);

      // A4 portrait: 210 × 297 mm. 6mm margin all around.
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pageW = 210;
      const pageH = 297;
      const margin = 6;
      const availW = pageW - 2 * margin;
      const availH = pageH - 2 * margin;

      const ratio = canvas.height / canvas.width;
      let renderW = availW;
      let renderH = renderW * ratio;
      if (renderH > availH) {
        renderH = availH;
        renderW = renderH / ratio;
      }
      // Top-align vertically so leftover space collects at the bottom
      // instead of a wasted strip above the content.
      const offsetX = (pageW - renderW) / 2;
      const offsetY = margin;

      pdf.addImage(imgData, 'JPEG', offsetX, offsetY, renderW, renderH);

      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = `${this.user.firstName ?? ''}-${this.user.lastName ?? ''}`
        .trim()
        .replace(/[\\/:*?"<>|\s]+/g, '-') || 'user';
      a.href = url;
      a.download = `history-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      console.error('Full-history PDF failed:', err);
      this.snackBar.openSnackBarError('שגיאה ביצירת הקובץ', 'x');
    } finally {
      this.fullPdfBusy = false;
    }
  }
}
